import { type ClassValue, clsx } from "clsx";
import {
  FormatDistanceOptions,
  format,
  formatDistance,
  parseISO,
} from "date-fns";
import { ptBR } from "date-fns/locale";
import { differenceBy } from "lodash";
import { twMerge } from "tailwind-merge";
import colors from "tailwindcss/colors";
import { z } from "zod";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export const parseDistanceByDate = (
  date: Date | string,
  options: FormatDistanceOptions = {},
) => {
  const dateToParse = typeof date === "string" ? parseISO(date) : date;

  const now = new Date();

  return formatDistance(dateToParse, now, {
    addSuffix: true,
    includeSeconds: true,
    locale: ptBR,
    ...options,
  });
};

export const acceptFileTypes = [
  "image/png",
  "image/jpeg",
  "image/jpg",
  "image/webp",
  "image/blob",
  "image/gif",
  "image/avif",
];

export const validateFileType = (data: FileList | File) => {
  return acceptFileTypes.includes(
    data instanceof FileList ? data?.[0]?.type : data?.type,
  );
};

export const isFileList = (value: unknown): value is FileList =>
  value instanceof FileList;

export type ColorKey = keyof typeof colors;

export type ExcludedTagColors =
  | "black"
  | "white"
  | "transparent"
  | "inherit"
  | "current";

const defaultExcludedColors: ExcludedTagColors[] = [
  "black",
  "white",
  "transparent",
  "inherit",
  "current",
];

export function getAvailableTagColors(
  excludeColorsFromParams = defaultExcludedColors,
) {
  return differenceBy(
    Object.keys(colors),
    defaultExcludedColors.concat(excludeColorsFromParams),
  ) as [ColorKey];
}

export function capitalizeFirstLetter(string: string) {
  return string.charAt(0).toUpperCase() + string.slice(1);
}

const colorOverrides = {
  warmGray: "stone",
  lightBlue: "sky",
};

export const isDeprecatedColorKey = (
  colorKey: string,
): colorKey is keyof typeof colorOverrides =>
  Object.keys(colorOverrides).includes(colorKey);

export const parseTagColor = (colorKey: string) => {
  let currentColor: (typeof colors)[keyof typeof colors];

  if (isDeprecatedColorKey(colorKey)) {
    const parsedColorKey = colorOverrides[colorKey];

    currentColor = colors[parsedColorKey as keyof typeof colors];
  } else {
    currentColor = colors[colorKey as keyof typeof colors] ?? colors.gray;
  }

  return currentColor;
};

type ColorShade =
  | 50
  | 100
  | 200
  | 300
  | 400
  | 500
  | 600
  | 700
  | 800
  | 900
  | 950;

export const getTagColor = (
  color: ColorKey | string,
  colorNumber: ColorShade = 600,
) => {
  return parseTagColor(color)[colorNumber];
};

export const formatApiDate = (
  date: string | Date | undefined | null,
  schema?: string,
) => {
  const formatterSchema = schema ?? "dddd/MM/yyyy ";

  if (!date) return "";

  if (typeof date === "string") {
    return format(new Date(date), formatterSchema);
  }

  return format(date, formatterSchema);
};

export const CACHE_TIME = {
  THIRTY_SECONDS: 30 * 1000,
  FIFTEEN_MINUTES: 15 * 60 * 1000,
  FIVE_MINUTES: 5 * 60 * 1000,
  ONE_HOUR: 60 * 60 * 1000,
  ONE_MINUTE: 60 * 1000,
  LIVE: 0,
  INFINITY: Infinity,
} as const;

export const normalizeString = (value: string = "") =>
  value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/ç/g, "c")
    .replace(/Ç/g, "C")
    .replace(" ", "-")
    .toLowerCase()
    .trim();

export const extractDomain = (url: string) => {
  return z
    .string()
    .url()
    .transform((vl) => {
      const url = new URL(vl);
      return {
        url: url.toString(),
        domain: url.hostname,
      };
    })
    .safeParse(url);
};

export const transformUrlStringInList = (text: string) =>
  text
    .split("\n")
    .map((url) => url.trim())
    .filter(Boolean);
